import { useEffect } from 'react';
import LogRocketService from 'services/logRocketService';
import { useStore } from './useStore';

/**
 * useLogRocket Hook
 *
 * React hook for easy LogRocket integration in components
 * Handles initialization and user identification automatically
 *
 * Features:
 * - Automatic initialization on mount
 * - Automatic user identification on authentication
 * - Easy access to tracking and error capture methods
 * - Type-safe API
 *
 * Usage:
 * ```tsx
 * const { trackEvent, captureError, getSessionURL, isActive } = useLogRocket();
 *
 * // Track an event
 * trackEvent('button_clicked', { buttonId: 'submit' });
 *
 * // Capture an error
 * try {
 *   await apiCall();
 * } catch (error) {
 *   captureError(error as Error, { component: 'MyComponent' });
 * }
 *
 * // Get session URL for support
 * const sessionUrl = getSessionURL();
 * ```
 */
export const useLogRocket = () => {
  const { UserStore } = useStore();
  const logRocketService = LogRocketService.getInstance();

  // Initialize LogRocket on mount
  useEffect(() => {
    if (logRocketService.shouldInitialize) {
      logRocketService.initialize();
    } else {
      console.warn('⚠️ LogRocket is disabled - skipping initialization');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  // Identify user when authenticated
  useEffect(() => {
    if (logRocketService.isActive && UserStore.isAuthenticated && UserStore.userMetadata) {
      logRocketService.identifyUser(UserStore.userMetadata as any);
    }
  }, [logRocketService, UserStore.isAuthenticated, UserStore.userMetadata]);

  // Return utility methods for use in components
  return {
    /**
     * Track a custom event
     * @param eventName - Name of the event
     * @param properties - Event properties
     */
    trackEvent: logRocketService.trackEvent.bind(logRocketService),

    /**
     * Capture an error with context
     * @param error - Error object
     * @param context - Additional context (component, action, etc.)
     */
    captureError: logRocketService.captureError.bind(logRocketService),

    /**
     * Get current session URL for debugging/support
     * @returns Session URL or null
     */
    getSessionURL: logRocketService.getSessionURL.bind(logRocketService),

    /**
     * Check if LogRocket is active
     */
    isActive: logRocketService.isActive,

    /**
     * Get detailed LogRocket status
     */
    getStatus: logRocketService.getStatus.bind(logRocketService),
  };
};
